import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Calculator, Plus } from 'lucide-react';
import { useMemo } from 'react';
import { EmptyState, PageHeader, Table, type TableColumn } from '~/components/data';
import { Button } from '~/components/primitives';
import type { BudgetListItem } from '~/lib/api/budgets';
import { fetchBudgets } from '~/lib/api/budgets';
import { formatDate, formatMoney } from '~/lib/format';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import { useActiveEntityStore } from '~/store/active-entity';
import shared from './_auth._shared.module.css';

/**
 * S16-FE.2 — Budgets list.
 */

export const Route = createFileRoute('/_auth/budgets/')({
  staticData: { minMainWidth: 960 },
  component: BudgetsPage,
});

const STATE_LABELS: Record<string, () => string> = {
  draft: m.budget_state_draft,
  pending_approval: m.budget_state_pending_approval,
  active: m.budget_state_active,
  locked: m.budget_state_locked,
  archived: m.budget_state_archived,
};

function BudgetsPage(): JSX.Element {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['budgets'],
    queryFn: () => fetchBudgets({ limit: 200 }),
  });
  const rows = query.data?.items ?? [];
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const entities = query.data?.entities ?? {};

  const columns: TableColumn<BudgetListItem>[] = useMemo(
    () => [
      {
        id: 'name',
        header: m.budget_column_name(),
        accessor: (r) => (
          <Link to="/budgets/$id" params={{ id: r.id }} className={shared.accentLink}>
            {r.name_ru}
          </Link>
        ),
        sticky: true,
      },
      ...(aggregateScope
        ? [
            {
              id: 'entity',
              header: m.project_col_entity(),
              accessor: (r: BudgetListItem) => entities[r.entity_id]?.name ?? '—',
              width: '180px',
            } satisfies TableColumn<BudgetListItem>,
          ]
        : []),
      {
        id: 'period',
        header: m.budget_column_period(),
        accessor: (r) => `${formatDate(r.period_from)} — ${formatDate(r.period_to)}`,
      },
      {
        id: 'rev',
        header: m.budget_column_revenue_plan(),
        accessor: (r) => formatMoney(r.total_revenue_plan),
        align: 'right',
        mono: true,
      },
      {
        id: 'cost',
        header: m.budget_column_cost_plan(),
        accessor: (r) => formatMoney(r.total_cost_plan),
        align: 'right',
        mono: true,
      },
      {
        id: 'state',
        header: m.budget_column_state(),
        accessor: (r) => (STATE_LABELS[r.state] ?? (() => r.state))(),
        width: '130px',
      },
    ],
    [aggregateScope, entities],
  );

  const scope = useUserScope();
  const canCreate = canDo(scope, 'budget', 'create');
  return (
    <div>
      <PageHeader
        title={m.phase2_budgets_title()}
        actions={
          // F38 (WHS-F38): создание — только роли с правом `budget:create`.
          canCreate ? (
            <Button
              variant="primary"
              leadingIcon={<Plus size={16} />}
              onClick={() => void navigate({ to: '/budgets/new' })}
            >
              {m.budget_button_new()}
            </Button>
          ) : undefined
        }
      />
      {rows.length === 0 && !query.isLoading ? (
        <EmptyState title={m.budget_empty_title()} icon={<Calculator size={32} />} />
      ) : (
        <Table<BudgetListItem>
          data={rows}
          columns={columns}
          rowKey={(r) => r.id}
          ariaLabel={m.phase2_budgets_title()}
          loading={query.isLoading}
          emptyTitle={m.budget_empty_title()}
        />
      )}
    </div>
  );
}
